import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormArray, FormGroup, Validators } from '@angular/forms';
import { UmraPackagesService, UmraPackageProperties, ShortlistedPackage, ShortlistRequest, ShortlistResponse } from '../services/umra-packages.service';
import { parseNumber } from 'devextreme/localization';
import {
  faHotel,
  faListSquares,
  faCheckCircle,
  faCalendarCheck,
  faClockRotateLeft,
  faPlane,
  faPlaneDeparture,
  faLocationDot,
  faLocationArrow,
  faBookBookmark,
  faKaaba,
  faRoute,
  faPlusCircle,
  faFileCirclePlus,
  faExclamationTriangle,
  faMosque,
  faTag,
  faMap,
  faSquareArrowUpRight,
  faInfoCircle,
  faRupeeSign,
  faFileLines,
  faListCheck,
  faCloudUpload,
  faXmarkCircle,
  faArrowRotateBack,
  faStarAndCrescent,
  faPerson,
  faPhone,
  faDoorOpen,
  faCommentDots,
} from '@fortawesome/free-solid-svg-icons';
import { faPeopleGroup } from '@fortawesome/free-solid-svg-icons/faPeopleGroup';
import { faEnvelope } from '@fortawesome/free-regular-svg-icons';

@Component({
  selector: 'app-umra-shortlisted-package',
  templateUrl: './umra-shortlisted-package.component.html',
  styleUrls: ['./umra-shortlisted-package.component.css']
})
export class UmraShortlistedPackageComponent implements OnInit {
  faHotel = faHotel;
  faDiamond = faListSquares;
  faCheckCircle = faCheckCircle;
  faExclamation = faExclamationTriangle;
  faCalendar = faCalendarCheck;
  faClock = faClockRotateLeft;
  faPlane = faPlane;
  faPlaneDeparture = faPlaneDeparture;
  faLocationDot = faLocationDot;
  faLocationArrow = faLocationArrow;
  faBookmark = faBookBookmark;
  faKaaba = faKaaba;
  faRoute = faRoute;
  faPlusCircle = faPlusCircle;
  faFileCirclePlus = faFileCirclePlus;
  faPeople = faPeopleGroup;
  faMosque = faMosque;
  faTag = faTag;
  faMarker = faMap;
  faEnvelope = faEnvelope;
  faSquareArrowUpRight = faSquareArrowUpRight;
  faInfoCircle = faInfoCircle;
  faRupeeSign = faRupeeSign;
  faFileLines = faFileLines;
  faPerson = faPerson;
  faPhone = faPhone;
  faDoor = faDoorOpen;
  faListCheck = faListCheck;
  faCloudUpload = faCloudUpload;
  faXmarkCircle = faXmarkCircle;
  faArrowLeft = faArrowRotateBack;
  faStar = faStarAndCrescent;
  faChat = faCommentDots;
  package: UmraPackageProperties | null = null;
  shortlistedPackage: ShortlistedPackage | null = null;
  shortlistForm: FormGroup;
  loading = false;
  submitted = false;
  error: string | null = null;
  successMessage: string | null = null;

  // Room type options
  roomTypes = [
    { value: 'Sharing', label: 'Sharing', price: 0 },
    { value: 'Quad', label: 'Quad', price: 0 },
    { value: 'Triple', label: 'Triple', price: 0 },
    { value: 'Double', label: 'Double', price: 0 }
  ];
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private packageService: UmraPackagesService,
    private fb: FormBuilder)
  {
    this.shortlistForm = this.fb.group({
      customerName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required, Validators.pattern('^[0-9+\\-\\s()]{10,15}$')]],
      preferredRoomType: ['Sharing', Validators.required],
      numberOfPassengers: [1, [Validators.required, Validators.min(1), Validators.max(50)]],
      specialRequirements: [''],
      remarks: [''],
      passengers: this.fb.array([]) 
    });
    // ... trigger initial update
    this.updatePassengerArray(1);
  }

  ngOnInit(): void {
    this.loadPackageDetails();
  }
  loadPackageDetails(): void {
    const packageId = this.route.snapshot.paramMap.get('id');

    if (!packageId) {
      this.error = 'No package ID provided';
      return;
    }

    this.loading = true;

    this.packageService.getAllPackages().subscribe({
      next: (response) => {
        if (response.success) {
          this.package = response.umraPackagesList.find(
            p => p.packageId === parseInt(packageId)
          ) || null;

          if (this.package) {
            this.updateRoomTypePrices();
          } else {
            this.error = 'Package not found';
          }
        } else {
          this.error = 'Failed to load package details';
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading package:', error);
        this.error = 'Failed to load package details';
        this.loading = false;
      }
    });
  }

  updateRoomTypePrices(): void {
    if (!this.package) return;

    this.roomTypes = [
      { value: 'Sharing', label: 'Sharing', price: this.package.priceSharing },
      { value: 'Quad', label: 'Quad', price: this.package.priceQuad },
      { value: 'Triple', label: 'Triple', price: this.package.priceTriple },
      { value: 'Double', label: 'Double', price: this.package.priceDouble },
    ];
  }
  // Modal control
  showPassengerModal = false;

  openPassengerModal(): void {
    this.showPassengerModal = true;
  }

  closePassengerModal(): void {
    this.showPassengerModal = false;
  }
  // Add a getter for the passengers FormArray
  get passengersFormArray(): FormArray {
    return this.shortlistForm.get('passengers') as FormArray;
  }
  maxpax: number = 50;
  minpax: number = 1;
  onRoomTypeChange(): void {
    const roomType = this.shortlistForm.get('preferredRoomType').value || "Sharing";
    switch (roomType) {
      case 'Sharing': this.minpax = 1; this.maxpax = 50; break;
      case 'Double': this.minpax = 2; this.maxpax = 2; this.shortlistForm.patchValue({ numberOfPassengers: 2 }); break;
      case 'Triple': this.minpax = 3; this.maxpax = 3; this.shortlistForm.patchValue({ numberOfPassengers: 3 }); break;
      case 'Quad': this.minpax = 4; this.maxpax = 4; this.shortlistForm.patchValue({ numberOfPassengers: 4 }); break;
      default: this.minpax = 1; this.maxpax = 50;
    }
    // Cap current number of passengers to the max
    const currentPax = this.shortlistForm.get('numberOfPassengers').value;
    if (currentPax > this.maxpax) {
      this.shortlistForm.patchValue({ numberOfPassengers: this.maxpax });
    }
    this.updatePassengerArray(this.shortlistForm.get('numberOfPassengers').value || 1);
  }
  onPassengerNumberChange(): void {
    const count = this.shortlistForm.get('numberOfPassengers').value || 1;
    this.updatePassengerArray(count);
  }
  updatePassengerArray(count: number): void {
    const arr = this.passengersFormArray;
    while (arr.length > count) {
      arr.removeAt(arr.length - 1);
    }
    while (arr.length < count) {
      arr.push(this.createPassengerGroup());
    }
  }
  createPassengerGroup(): FormGroup {
    return this.fb.group({
      passportNumber: ['', Validators.required],
      passportIssueDate: ['', Validators.required],
      passportExpiryDate: ['', Validators.required],
      surname: ['', Validators.required],
      givenName: ['', Validators.required],
      dateOfBirth: ['', Validators.required],
      childWithoutBed: [false],
      infantWithoutBed: [true],
      gender: ['', Validators.required]
    });
  }
  getSelectedRoomPrice(): number {
    const selectedType = this.shortlistForm.get('preferredRoomType').value;
    const roomType = this.roomTypes.find(r => r.value === selectedType);
    return roomType ? roomType.price : 0;
  }
  estimatedTotalPrice: number;
  calculateTotalPrice(): number {
    const passengers = this.shortlistForm.get('numberOfPassengers').value || 1;
    this.estimatedTotalPrice = (this.getSelectedRoomPrice() * passengers) - (passengers * this.package.discountOnSupplier);
    return this.estimatedTotalPrice;
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.shortlistForm.invalid || this.passengersFormArray.invalid || this.passengersFormArray.length === 0) {
      // mark all passenger fields as touched to show errors
      this.passengersFormArray.controls.forEach(c => c.markAllAsTouched());
      return;
    }
    if (this.shortlistForm.get('numberOfPassengers').value > this.package.availableSeats) {
      this.error = `Only ${this.package.availableSeats} seats are available for this package. Please reduce the number of passengers.`;
      return;
    }
    this.loading = true;
    this.error = null;
    this.successMessage = null;

    const formData = this.shortlistForm.value;

    // DIRECT MAPPING: Create ShortlistedPackage from UmraPackageProperties
    const shortlistedPackage: ShortlistedPackage = {
      // Package details from selected package
      id: 0,
      packageId: this.package.packageId,
      packageTitle: this.package.packageTitle,
      airlineName: this.package.airlineName,
      routeStart: this.package.routeStart,
      routeEnd: this.package.routeEnd,
      travelDate: this.package.travelDate,
      arrivalDate: this.package.arrivalDate,
      nightsCount: this.package.nightsCount,
      makkahHotel: this.package.makkahHotel,
      madinahHotel: this.package.madinahHotel,
      priceSharing: this.package.priceSharing,
      priceDouble: this.package.priceDouble,
      priceTriple: this.package.priceTriple,
      priceQuad: this.package.priceQuad,
      availableSeats: this.package.availableSeats,
      badgeText: this.package.badgeText,
      groupCode: this.package.groupCode,
      packageCode: this.package.packageCode,
      provider: this.package.provider,
      isActive: this.package.isActive,
      agencyId: localStorage.getItem("AgencyId"),
      userId: parseInt(localStorage.getItem("UserId")),
      departureETD: this.package.departureETD,
      departureETA: this.package.departureETA,
      arrivalETD: this.package.arrivalETD,
      arrivalETA: this.package.arrivalETA,
      departureFligntNo: this.package.departureFligntNo,
      arrivalFligntNo: this.package.arrivalFligntNo,
      serviceChargesPerPassenger: this.package.markupOnSupplier,
      discountPerPassenger: this.package.discountOnSupplier,
      netFromCustomer: this.estimatedTotalPrice,
      // User input from form
      customerName: formData.customerName,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      preferredRoomType: formData.preferredRoomType,
      numberOfPassengers: formData.numberOfPassengers,
      specialRequirements: formData.specialRequirements || '',
      remarks: formData.remarks || '',
      shortlistStatus: 'Pending',
      shortlistedDate: new Date(),
      createdDate: new Date(),
      customPackageId: this.package.customPackageId,
      providerType: this.package.providerType,
      supplierLetter: this.package.supplierLetter,
      passengers: formData.passengers.map(p => ({
        passportNumber: p.passportNumber,
        passportIssueDate: p.passportIssueDate,
        passportExpiryDate: p.passportExpiryDate,
        surname: p.surname,
        givenName: p.givenName,
        dateOfBirth: p.dateOfBirth,
        childWithoutBed: p.childWithoutBed,
        infantWithoutBed: p.infantWithoutBed,
        gender: p.gender
      }))
    };

    // Create the request
    const shortlistRequest: ShortlistRequest = {
      package: shortlistedPackage,
      customerName: formData.customerName,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      preferredRoomType: formData.preferredRoomType,
      numberOfPassengers: formData.numberOfPassengers,
      specialRequirements: formData.specialRequirements || '',
      remarks: formData.remarks || '',
      passengers: formData.passengers.map(p => ({
        passportNumber: p.passportNumber,
        passportIssueDate: p.passportIssueDate,
        passportExpiryDate: p.passportExpiryDate,
        surname: p.surname,
        givenName: p.givenName,
        dateOfBirth: p.dateOfBirth,
        childWithoutBed: p.childWithoutBed,
        infantWithoutBed: p.infantWithoutBed,
        gender: p.gender
      }))
    };

    // Call API
    this.packageService.shortlistPackage(shortlistRequest).subscribe({
      next: (response: ShortlistResponse) => {
        if (response.success) {
          this.successMessage = `Package shortlisted successfully! Reference ID: ${response.shortlistId}`;
          this.shortlistForm.reset({
            preferredRoomType: 'Sharing',
            numberOfPassengers: 1
          });
          this.submitted = false;
        } else {
          this.error = response.message || 'Failed to shortlist package';
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.error = 'Failed to shortlist package. Please try again.';
        this.loading = false;
      }
    });
  }
  goBack(): void {
    this.router.navigate(['/umra-offers']);
  }
  goHome(): void {
    this.router.navigate(['/flightSearch']);
  }
  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // Getter for easy form control access
  get f() {
    return this.shortlistForm.controls;
  }
}
